/** @jsxImportSource @emotion/react */
import React, { FC } from 'react';
import { generatePath, Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  css,
  darken,
  Divider,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import { isToday } from 'date-fns';
import { useTelegramUser } from 'hooks/useTelegramUser';
import WeekDayTotalLabel from 'pages/Week/components/WeekDayTotalLabel/WeekDayTotalLabel';
import { useGetOrderApi } from 'pages/Week/hooks/useGetOrderApi';
import { ROUTES } from 'utils/routes';
import { Props } from './types';

const WeekItem: FC<Props> = ({ week, dayDate }) => {
  const theme = useTheme();
  const { id: userId } = useTelegramUser();
  const isActive = week[dayDate].active;

  // TODO propagate props totalAmount to label
  const { order } = useGetOrderApi(dayDate);

  const menuRoute = generatePath(ROUTES.MENU_CURRENT_DAY, {
    dayDate,
    userId: `${userId}`,
  });

  const checkoutRoute = generatePath(ROUTES.CHECKOUT_WITH_ORDER, {
    dayDate,
    orderId: String(order?.id),
    userId: `${userId}`,
  });

  const isCurrentDay = isToday(new Date(dayDate));

  return (
    <Card
      component={isActive ? Link : Box}
      to={order?.id ? checkoutRoute : menuRoute}
      elevation={1}
      aria-disabled={!isActive}
      css={css`
        background-color: ${darken(theme.palette.primary.main, 0.8)};
        display: block;
        text-decoration: none;
        ${isCurrentDay
          ? `border: 2px solid ${theme.palette.primary.light}`
          : ''}
      `}
    >
      <CardContent
        css={css`
          opacity: ${isActive ? 1 : 0.3};
          &:last-child {
            padding-bottom: 16px;
          }
        `}
      >
        <Grid container spacing={2}>
          <Grid item minWidth={100} alignItems="center">
            <Typography
              align="center"
              css={css`
                font-size: ${isActive ? '72px' : '60px'};
              `}
            >
              {week[dayDate].day}
            </Typography>
          </Grid>

          <Grid item>
            <Divider
              orientation="vertical"
              css={css`
                border-color: ${theme.palette.primary.dark};
              `}
            />
          </Grid>

          <Grid item>
            <Typography color="primary" variant="h1" gutterBottom>
              {week[dayDate].label}
            </Typography>

            <Typography
              variant="body2"
              gutterBottom
              css={css`
                opacity: 0.7;
              `}
            >
              {week[dayDate].date}
            </Typography>

            <WeekDayTotalLabel dayDate={dayDate} />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default WeekItem;
